import React, { useState, useEffect } from "react";
import { devicesAPI } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Monitor,
  Server,
  Laptop,
  Wifi,
  Printer,
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  Edit,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const deviceIcons = {
  server: Server,
  workstation: Monitor,
  laptop: Laptop,
  network: Wifi,
  iot: Printer,
};

export const DevicesPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    device_type: "workstation",
    ip_address: "",
    mac_address: "",
    os_type: "",
    os_version: "",
    status: "online",
  });

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(() => fetchDevices(), 30000);
    return () => clearInterval(interval);
  }, [currentPage]);

  const fetchDevices = async (page = currentPage) => {
    try {
      const response = await devicesAPI.getAll(page, 20);
      setDevices(response.items);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      toast.error("Failed to fetch devices");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDevice) {
        await devicesAPI.update(editingDevice.id, formData);
        toast.success("Device updated successfully");
      } else {
        await devicesAPI.create(formData);
        toast.success("Device added successfully");
      }
      fetchDevices();
      handleCloseDialog();
    } catch (err) {
      toast.error(
        editingDevice ? "Failed to update device" : "Failed to add device",
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this device?")) return;
    try {
      await devicesAPI.delete(id);
      toast.success("Device deleted successfully");
      fetchDevices();
    } catch (err) {
      toast.error("Failed to delete device");
    }
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      name: device.name,
      device_type: device.device_type,
      ip_address: device.ip_address,
      mac_address: device.mac_address,
      os_type: device.os_type,
      os_version: device.os_version,
      status: device.status,
    });
    setShowAddDialog(true);
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingDevice(null);
    setFormData({
      name: "",
      device_type: "workstation",
      ip_address: "",
      mac_address: "",
      os_type: "",
      os_version: "",
      status: "online",
    });
  };

  const filteredDevices = devices.filter((device) => {
    const matchesSearch =
      device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.ip_address.includes(searchQuery);
    const matchesType =
      filterType === "all" || device.device_type === filterType;
    const matchesSource =
      filterSource === "all" || device.source === filterSource;
    return matchesSearch && matchesType && matchesSource;
  });

  // Count agent-installed devices
  const agentDeviceCount = devices.filter((d) => d.source === "agent").length;

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="devices-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="devices-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
            Devices
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and monitor your device inventory
          </p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          data-testid="add-device-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Device
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search devices by name or IP..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="device-search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger
                className="w-full sm:w-40"
                data-testid="device-type-filter"
              >
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="server">Servers</SelectItem>
                <SelectItem value="workstation">Workstations</SelectItem>
                <SelectItem value="laptop">Laptops</SelectItem>
                <SelectItem value="network">Network</SelectItem>
                <SelectItem value="iot">IoT</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger
                className="w-full sm:w-44"
                data-testid="device-source-filter"
              >
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="agent">
                  Agent Installed ({agentDeviceCount})
                </SelectItem>
                <SelectItem value="manual">Manual Entry</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Devices Table */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">
            Device Inventory ({filteredDevices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>OS</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Security Score</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDevices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No devices found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDevices.map((device) => {
                    const Icon = deviceIcons[device.device_type] || Monitor;
                    return (
                      <TableRow
                        key={device.id}
                        data-testid={`device-row-${device.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-muted">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{device.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {device.mac_address}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {device.device_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              device.source === "agent" ? "default" : "outline"
                            }
                            className={
                              device.source === "agent"
                                ? "bg-green-100 text-green-800 hover:bg-green-100"
                                : ""
                            }
                          >
                            {device.source === "agent" ? "🟢 Agent" : "Manual"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="ip-address">
                            {device.ip_address}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{device.os_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {device.os_version}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span
                              className={`status-dot ${device.status === "online" ? "status-online" : "status-offline"}`}
                            ></span>
                            <span className="capitalize text-sm">
                              {device.status}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`font-heading font-bold ${getScoreColor(device.security_score)}`}
                          >
                            {device.security_score}
                          </span>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`device-menu-${device.id}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleEdit(device)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(device.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => { setCurrentPage(p => p - 1); }}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => { setCurrentPage(p => p + 1); }}>
            Next
          </Button>
        </div>
      )}

      {/* Add/Edit Device Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md" data-testid="device-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingDevice ? "Edit Device" : "Add New Device"}
            </DialogTitle>
            <DialogDescription>
              {editingDevice
                ? "Update device information"
                : "Add a new device to your inventory"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Device Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Web Server 01"
                required
                data-testid="device-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="device_type">Type</Label>
                <Select
                  value={formData.device_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, device_type: value })
                  }
                >
                  <SelectTrigger data-testid="device-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="server">Server</SelectItem>
                    <SelectItem value="workstation">Workstation</SelectItem>
                    <SelectItem value="laptop">Laptop</SelectItem>
                    <SelectItem value="network">Network</SelectItem>
                    <SelectItem value="iot">IoT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger data-testid="device-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ip_address">IP Address</Label>
              <Input
                id="ip_address"
                value={formData.ip_address}
                onChange={(e) =>
                  setFormData({ ...formData, ip_address: e.target.value })
                }
                placeholder="e.g., 192.168.1.100"
                required
                data-testid="device-ip-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mac_address">MAC Address</Label>
              <Input
                id="mac_address"
                value={formData.mac_address}
                onChange={(e) =>
                  setFormData({ ...formData, mac_address: e.target.value })
                }
                placeholder="e.g., 00:1A:2B:3C:4D:5E"
                required
                data-testid="device-mac-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="os_type">OS Type</Label>
                <Input
                  id="os_type"
                  value={formData.os_type}
                  onChange={(e) =>
                    setFormData({ ...formData, os_type: e.target.value })
                  }
                  placeholder="e.g., Windows"
                  required
                  data-testid="device-os-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="os_version">OS Version</Label>
                <Input
                  id="os_version"
                  value={formData.os_version}
                  onChange={(e) =>
                    setFormData({ ...formData, os_version: e.target.value })
                  }
                  placeholder="e.g., Windows 11"
                  required
                  data-testid="device-os-version-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="device-submit-btn">
                {editingDevice ? "Update" : "Add Device"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
